import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { MonthlyClosing, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, writeAuditLog } from '@shared/utils';
import { recomputeAllCustomerRanks } from '@modules/customer';
import { deriveCurrentStates, eventMillis } from '../utils/closing-state';

const yearMonthSchema = z.string().regex(
  /^\d{4}-(0[1-9]|1[0-2])$/,
  'yearMonth は YYYY-MM 形式（例: 2026-05）で入力してください'
);

const INV_COL = 'invoices';
const CLOSING_COL = 'monthly_closings';

// 締め記録はイベントログ（追記型）。1ドキュメント = 1イベント。
// action 未設定の旧データは締めイベント（'close'）として扱う。
function toEvent(d: { id: string; data: () => Record<string, unknown> }): MonthlyClosing {
  return { id: d.id, action: 'close', ...d.data() } as MonthlyClosing;
}

export async function fetchMonthlyClosings(): Promise<MonthlyClosing[]> {
  const snap = await getDocs(
    query(collection(db, CLOSING_COL), where('isDeleted', '==', false))
  );
  // 対象月の新しい順（同一月内は締め日時の新しい順）に並べる。
  return snap.docs.map(toEvent).sort((a, b) => {
    if (a.yearMonth !== b.yearMonth) return a.yearMonth < b.yearMonth ? 1 : -1;
    return eventMillis(b.closedAt) - eventMillis(a.closedAt);
  });
}

/** 指定月の現在状態（最新イベント）。締め履歴が無ければ null。 */
export async function fetchClosingStatus(yearMonth: string): Promise<MonthlyClosing | null> {
  const q = query(
    collection(db, CLOSING_COL),
    where('yearMonth', '==', yearMonth),
    where('isDeleted', '==', false)
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return deriveCurrentStates(snap.docs.map(toEvent)).get(yearMonth) ?? null;
}

/** 対象月の請求書ロックを一括で切り替える。集計値も返す。 */
async function lockMonthInvoices(
  yearMonth: string,
  userId: ID,
  lock: boolean
): Promise<{ totalInvoiced: number; totalPaid: number; totalOutstanding: number }> {
  const [year, month] = yearMonth.split('-').map(Number);
  const startDate = Timestamp.fromDate(new Date(year, month - 1, 1));
  const endDate = Timestamp.fromDate(new Date(year, month, 1));

  const snap = await getDocs(
    query(
      collection(db, INV_COL),
      where('isDeleted', '==', false),
      where('issueDate', '>=', startDate),
      where('issueDate', '<', endDate)
    )
  );

  let totalInvoiced = 0;
  let totalPaid = 0;
  let totalOutstanding = 0;

  const batch = writeBatch(db);
  snap.docs.forEach((d) => {
    const data = d.data();
    const status = data['status'] as string;
    if (status === 'issued' || status === 'partially_paid') {
      totalInvoiced += data['total'] as number;
      totalPaid += data['paidAmount'] as number;
      totalOutstanding += data['remainingAmount'] as number;
      // 締め時はロック、解除時は元ロック済みのものだけ解除する。
      if (lock || data['isLocked'] === true) {
        batch.update(d.ref, { isLocked: lock, ...buildUpdateFields(userId) });
      }
    } else if (status === 'paid') {
      totalInvoiced += data['total'] as number;
      totalPaid += data['paidAmount'] as number;
    }
  });

  await batch.commit();
  return { totalInvoiced, totalPaid, totalOutstanding };
}

export async function performMonthlyClosing(
  yearMonth: string,
  userId: ID
): Promise<MonthlyClosing> {
  parseOrThrow(yearMonthSchema, yearMonth);

  const states = deriveCurrentStates(await fetchMonthlyClosings());
  const current = states.get(yearMonth);
  if (current && current.action === 'close') {
    throw new Error(`${yearMonth} は既に締め処理済みです。`);
  }

  // ロック忘れ防止: 他に解除中のままの月があれば、新たな締め処理を禁止する。
  const stillUnlocked = [...states.values()]
    .filter((e) => e.action === 'unlock' && e.yearMonth !== yearMonth)
    .map((e) => e.yearMonth)
    .sort();
  if (stillUnlocked.length > 0) {
    throw new Error(
      `${stillUnlocked.join('、')} が解除中のままです。先に再締めしてからこの月を締めてください。`
    );
  }

  const totals = await lockMonthInvoices(yearMonth, userId, true);

  const closingPayload = {
    yearMonth,
    action: 'close' as const,
    closedAt: Timestamp.now(),
    closedBy: userId,
    ...totals,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, CLOSING_COL), closingPayload);

  await writeAuditLog({
    entityType: 'monthly_closing',
    entityId: ref.id,
    action: 'create',
    userId,
    after: { yearMonth, action: 'close', ...totals },
  });

  // 締め確定後に顧客ランクを取引実績から自動再計算する。
  // 締めは既に確定済みのため、再計算が失敗しても締め処理自体は失敗させず監査ログに残す。
  try {
    await recomputeAllCustomerRanks(userId);
  } catch (e) {
    await writeAuditLog({
      entityType: 'customer',
      entityId: 'rank_recompute',
      action: 'update',
      userId,
      after: { error: e instanceof Error ? e.message : String(e), trigger: yearMonth },
    });
  }

  return { id: ref.id, ...closingPayload } as unknown as MonthlyClosing;
}

/** 月次締めを解除する（請求書ロック解除 + 解除イベントを追記）。 */
export async function unlockMonthlyClosing(
  yearMonth: string,
  userId: ID
): Promise<MonthlyClosing> {
  parseOrThrow(yearMonthSchema, yearMonth);

  const current = deriveCurrentStates(await fetchMonthlyClosings()).get(yearMonth);
  if (!current || current.action !== 'close') {
    throw new Error(`${yearMonth} は締め処理されていないため解除できません。`);
  }

  await lockMonthInvoices(yearMonth, userId, false);

  const unlockPayload = {
    yearMonth,
    action: 'unlock' as const,
    closedAt: Timestamp.now(),
    closedBy: userId,
    totalInvoiced: current.totalInvoiced,
    totalPaid: current.totalPaid,
    totalOutstanding: current.totalOutstanding,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, CLOSING_COL), unlockPayload);

  await writeAuditLog({
    entityType: 'monthly_closing',
    entityId: ref.id,
    action: 'update',
    userId,
    after: { yearMonth, action: 'unlock' },
  });

  return { id: ref.id, ...unlockPayload } as unknown as MonthlyClosing;
}

/**
 * 指定月の締めを丸ごと削除する。
 * 締めイベント（締め/解除の全履歴）を論理削除し、請求書ロックを解除して「未締め」に戻す。
 */
export async function deleteMonthlyClosing(
  yearMonth: string,
  userId: ID
): Promise<void> {
  parseOrThrow(yearMonthSchema, yearMonth);

  const snap = await getDocs(
    query(
      collection(db, CLOSING_COL),
      where('yearMonth', '==', yearMonth),
      where('isDeleted', '==', false)
    )
  );
  if (snap.empty) throw new Error(`${yearMonth} の締め記録がありません。`);

  // 請求書のロックを解除する。
  await lockMonthInvoices(yearMonth, userId, false);

  // その月の締めイベントをすべて論理削除する。
  const batch = writeBatch(db);
  snap.docs.forEach((d) => {
    batch.update(d.ref, { isDeleted: true, ...buildUpdateFields(userId) });
  });
  await batch.commit();

  await writeAuditLog({
    entityType: 'monthly_closing',
    entityId: yearMonth,
    action: 'delete',
    userId,
    after: { yearMonth, action: 'delete_all', deletedCount: snap.docs.length },
  });
}

export async function unlockInvoiceByAdmin(
  invoiceId: ID,
  userId: ID
): Promise<void> {
  const ref = doc(db, INV_COL, invoiceId);
  await updateDoc(ref, { isLocked: false, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'invoice',
    entityId: invoiceId,
    action: 'update',
    userId,
    after: { isLocked: false, reason: '管理者による締め解除' },
  });
}
